if (!customElements.get('sticky-atc')) {
    class StickyAtc extends HTMLElement {
        #footerObserver = null;
        #blocking = new Set();
        #elHero = null;
        #elFooter = null;
        #scrollFrame = null;

        #handleScroll = () => {
            if (this.#scrollFrame) {
                return;
            }

            this.#scrollFrame = requestAnimationFrame(() => {
                this.#scrollFrame = null;
                this.#updateHeroBlocking();
            });
        };

        #handleResize = () => {
            this.#updateHeroBlocking();
        };

        connectedCallback() {
            this.#elHero = this.#resolve('hero-selector');
            this.#elFooter = this.#resolve('footer-selector');

            // Nothing to gate against: reveal the bar for the whole page.
            if (!this.#elHero && !this.#elFooter) {
                this.removeAttribute('data-stowed');
                this.setAttribute('data-ready', '');
                this.#syncAvailability();
                return;
            }

            this.#observeFooter();
            this.#bindHero();
            this.#syncAvailability();

            // Wait for layout so the hero's measured height is accurate on load.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.#updateHeroBlocking();
                    this.setAttribute('data-ready', '');
                    this.#syncAvailability();
                });
            });
        }

        disconnectedCallback() {
            this.#footerObserver?.disconnect();
            cancelAnimationFrame(this.#scrollFrame);
            window.removeEventListener('scroll', this.#handleScroll);
            window.removeEventListener('resize', this.#handleResize);
        }

        #resolve(attribute) {
            const selector = this.getAttribute(attribute);
            return selector ? document.querySelector(selector) : null;
        }

        #setBlocking(target, isBlocking) {
            if (isBlocking) {
                this.#blocking.add(target);
            } else {
                this.#blocking.delete(target);
            }

            this.toggleAttribute('data-stowed', this.#blocking.size > 0);
            this.#syncAvailability();
        }

        #syncAvailability() {
            const isHidden = this.hasAttribute('data-stowed') || !this.hasAttribute('data-ready');

            this.toggleAttribute('inert', isHidden);
            this.setAttribute('aria-hidden', String(isHidden));
        }

        #observeFooter() {
            if (!this.#elFooter) {
                return;
            }

            this.#footerObserver = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    this.#setBlocking(entry.target, entry.isIntersecting);
                }
            });

            this.#footerObserver.observe(this.#elFooter);
        }

        #bindHero() {
            if (!this.#elHero) {
                return;
            }

            window.addEventListener('scroll', this.#handleScroll, { passive: true });
            window.addEventListener('resize', this.#handleResize);
        }

        #updateHeroBlocking() {
            if (!this.#elHero) {
                return;
            }

            const { bottom } = this.#elHero.getBoundingClientRect();
            const atPageTop = window.scrollY <= 0;
            const hasPassedThreshold = bottom <= window.innerHeight;

            // Keep stowed at the absolute top of the page, and until the hero
            // bottom reaches the bottom of the viewport.
            this.#setBlocking(this.#elHero, atPageTop || !hasPassedThreshold);
        }
    }

    customElements.define('sticky-atc', StickyAtc);
}
